import * as React from 'react';

import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import { useGetGateway } from 'src/features/gateway/api/useGetGateway';
import { GATEWAY_IDENTIFIER_TYPE_MAP } from 'src/features/gateway/utils/constants';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import GatewayPricingPlans from 'src/features/gateway/components/GatewayPricingPlans';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import { Button } from 'src/registry/ui/Button';
import { Separator } from 'src/registry/ui/Separator';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useParking } from 'src/providers/ParkingProvider';
import { generalMessages } from 'src/utils/messages';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { BOOLEAN_MAP } from 'src/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

export default function GatewayDashboard() {
    const { formatMessage, locale } = useIntl();
    const { gatewayId = '' } = useParams();
    const { parkingId } = useParking();

    const { data: gatewayData } = useGetGateway({ gatewayId, parkingId });

    const accessZones = useHasPermissions([PERMISSION.ACCESS_ZONES]);
    const managePricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);

    const { setTitle, removeTitle } = useTitleStore();
    React.useEffect(() => {
        if (!gatewayData) return;
        setTitle(gatewayData.translations.find((translation) => translation.language === locale)?.name ?? '');
        return removeTitle;
    }, [locale, removeTitle, setTitle, gatewayData]);

    return (
        <>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to={`/${PATH.ADMINISTRATION}/gateway`}>
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(gatewayMessages.backToAllGateways)}
                </Link>
            </Button>
            <GeneralBlock>
                <div className="grid flex-3 grid-cols-3 gap-4">
                    {gatewayData?.translations.map((translation) => {
                        const languageToUpper = translation.language.toUpperCase();
                        return (
                            <GeneralBlockAttribute
                                key={languageToUpper}
                                label={`${formatMessage(generalMessages.name)} (${languageToUpper})`}
                                value={translation.name}
                            />
                        );
                    })}
                </div>
                <Separator />
                <div className="grid flex-3 grid-cols-3 gap-4">
                    {gatewayData?.entry_zone ? (
                        gatewayData.entry_zone.translations.map((translation) => {
                            const languageToUpper = translation.language.toUpperCase();
                            return (
                                <GeneralBlockAttribute
                                    key={languageToUpper}
                                    label={`${formatMessage(gatewayMessages.entryZone)} (${languageToUpper})`}
                                    value={translation.name}
                                    link={
                                        accessZones
                                            ? `/${PATH.ADMINISTRATION}/zone/${gatewayData.entry_zone.id}`
                                            : undefined
                                    }
                                />
                            );
                        })
                    ) : (
                        <GeneralBlockAttribute label={formatMessage(gatewayMessages.entryZone)} />
                    )}
                    {gatewayData?.exit_zone ? (
                        gatewayData.exit_zone.translations.map((translation) => {
                            const languageToUpper = translation.language.toUpperCase();
                            return (
                                <GeneralBlockAttribute
                                    key={languageToUpper}
                                    label={`${formatMessage(gatewayMessages.exitZone)} (${languageToUpper})`}
                                    value={translation.name}
                                    link={
                                        accessZones
                                            ? `/${PATH.ADMINISTRATION}/zone/${gatewayData.exit_zone.id}`
                                            : undefined
                                    }
                                />
                            );
                        })
                    ) : (
                        <GeneralBlockAttribute label={formatMessage(gatewayMessages.exitZone)} />
                    )}
                    <GeneralBlockAttribute
                        label={formatMessage(gatewayMessages.requiredIdentifiers)}
                        value={gatewayData?.required_identifier_types
                            .map((type) => formatMessage(GATEWAY_IDENTIFIER_TYPE_MAP[type]))
                            .join(', ')}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(gatewayMessages.allowUnknownMainIdentifier)}
                        value={
                            gatewayData?.allow_unknown_main_identifier !== undefined
                                ? formatMessage(
                                      BOOLEAN_MAP[
                                          String(gatewayData.allow_unknown_main_identifier) as 'true' | 'false'
                                      ],
                                  )
                                : '-'
                        }
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(gatewayMessages.requireAllIdentifier)}
                        value={
                            gatewayData?.require_all_identifier !== undefined
                                ? formatMessage(
                                      BOOLEAN_MAP[String(gatewayData.require_all_identifier) as 'true' | 'false'],
                                  )
                                : '-'
                        }
                    />
                </div>
                <Separator />
                <div className="grid flex-3 grid-cols-3 gap-4">
                    {gatewayData?.translations.map((translation) => {
                        const languageToUpper = translation.language.toUpperCase();
                        return (
                            <GeneralBlockAttribute
                                label={`${formatMessage(generalMessages.description)} (${languageToUpper})`}
                                value={translation.description}
                                key={translation.id}
                            />
                        );
                    })}
                </div>
            </GeneralBlock>
            {managePricingPlans && (
                <Tabs defaultValue="pricing-plans">
                    <TabsList>
                        <TabsTrigger value="pricing-plans">
                            <FormattedMessage
                                defaultMessage={pricingPlanMessages.pricingPlans.defaultMessage}
                                id={pricingPlanMessages.pricingPlans.id}
                            />
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="pricing-plans">
                        <GatewayPricingPlans gatewayId={gatewayId} />
                    </TabsContent>
                </Tabs>
            )}
        </>
    );
}
